import { useEffect, useRef, useState } from "react";
import { postData, useGetData } from "../../services/api/api-tools";
import LogoPerception from '../../assets/images/logos/logo-blue-black.png';
import LogoLISI from '../../assets/images/logos/LISI_Logo.png';
import { useSocket } from "../../contexts/SocketContext";
import { useAlert } from "../../contexts/AlertContext";
import { Loader } from "../../components/loading/Loader";
import { formatSecondsToMinutesSeconds } from "../../helpers/date/date-helper";
import { ArrowPathRoundedSquareIcon, PowerIcon } from "@heroicons/react/20/solid";

export const LISILivePage = () => {

  const { socket, subscribeToEvent, unsubscribeFromEvent, sendEvent } = useSocket();
  const { showAlert } = useAlert();

  const { data: patterns } = useGetData('patterns', `patterns`);

  const [selectedPattern, setSelectedPattern] = useState(null);
  const [isStarted, setIsStarted] = useState(true);
  const [currentData, setCurrentData] = useState({});
  const [showSynthesis, setShowSynthesis] = useState(false);

  const lockedData = useRef(false);

  /* Temporary useEffect to automatically use the first pattern */
  useEffect(() => {
    if (!patterns) return;
    if (patterns?.length === 0) return;
    setSelectedPattern(patterns[0]);
  }, [patterns]);

  const handleStart = () => {
    if(selectedPattern !== null)
      setIsStarted(true);

    /* Send pattern data to the engine */
    // sendEvent('selected_pattern', selectedPattern);
  };

  const resetInterface = () => {
    setCurrentData([]);
    lockedData.current = false;
    handleStart();
  };

  useEffect(() => {
    if (!isStarted) return;
    if (!socket) return;

    const handleReceivedData = (data) => {
      if (lockedData.current) return;
      setCurrentData(prevData => ({...prevData, ...data}));
    };

    subscribeToEvent('current_data', handleReceivedData);

    return () => {
      unsubscribeFromEvent('current_data', handleReceivedData);
    };
  }, [socket, isStarted]);

  const handleRaiseError = () => {
    console.log('New error raised.');
    postData('live_errors', {date: Math.floor(Date.now() / 1000), pattern: `/api/patterns/${selectedPattern?.id}`});
    showAlert('success', 'Le manquement a bien été remonté.')
  };

  const handleResetCount = () => {
    sendEvent('current_data', { reset_count: true });
  };

  const handleReset = () => {
    sendEvent('current_data', { reset: true });
  };

  const stopProcess = () => {
    lockedData.current = true;
    setShowSynthesis(true);
    setTimeout(resetInterface, 30000);
  };

  return (
    <div className='h-full overflow-hidden'>
      <div className='flex flex-col items-center justify-center w-full h-full'>
        {
          isStarted && selectedPattern !== null ?
            <div className="w-full h-full flex gap-12 p-4 text-white text-3xl bg-perception-black-800">

              {/* Left column */}
              <div className="w-2/3 h-full flex flex-col justify-start p-4 gap-4 rounded-md bg-perception-black-500 truncate">
                <h1 className="text-center p-4 font-bold bg-perception-blue rounded-md">Zone enfournement</h1>

                {/* Left part */}
                <div className="w-full h-full flex justify-center items-start gap-4">
                  <div className="w-1/2 h-full flex flex-col justify-between gap-4">

                    <div className="flex flex-col justify-start gap-4">
                      {/* Pieces number */}
                      <div className="h-1/3 p-8 relative rounded-md flex bg-perception-gray-800 items-center">
                        Pièces dans le four : {currentData?.pieces_count !== undefined ? currentData.pieces_count : '-'}
                      </div>

                      {/* Total count */}
                      <div className="h-1/3 flex items-center p-8 bg-perception-gray-800 rounded-md">
                        Nombre total de pièces : {currentData?.pieces_hit !== undefined && currentData?.pieces_hit !== null ? currentData.pieces_hit : '-'}
                      </div>

                      {/* Transfer time */}
                      <div className="h-1/3 flex items-center p-8 bg-perception-gray-800 rounded-md">
                        Durée de transfert : {currentData?.transfer_duration !== undefined && currentData?.transfer_duration !== null ?
                        Math.ceil(currentData.transfer_duration * 10) / 10 + 's' : '-'}
                      </div>
                    </div>

                    {/* Action buttons */}
                    <div className="flex justify-center items-center gap-4 p-4 rounded-md bg-perception-black-500">
                      <button className="bg-error text-white text-xl p-4 rounded-md flex items-center" onClick={handleResetCount}>
                        <ArrowPathRoundedSquareIcon aria-hidden="true" className="size-5 mr-2" /> RAZ compteur
                      </button>
                      <button className="bg-error text-white text-xl p-4 rounded-md flex items-center" onClick={handleReset}>
                        <PowerIcon aria-hidden="true" className="size-5 mr-2" /> Réinitialiser
                      </button>
                    </div>

                    {/* Logos */}
                    <div className="p-8 flex justify-center items-center text-xl gap-4 text-black">
                      <img src={LogoPerception} alt="logo-perception" className="max-w-[200px] max-h-[60px]" />
                      x
                      <img src={LogoLISI} alt="logo-LCA" className="max-w-[200px] max-h-[60px]" />
                    </div>
                  </div>

                  {/* Middle part */}
                  <div className="w-1/2 h-full flex flex-col justify-between gap-4">
                    {/* Heater durations */}
                    <div className="h-full justify-between items-center bg-perception-gray-800 rounded-md overflow-auto">
                      <table className="w-full">
                        <thead>
                        <tr className="bg-perception-gray-600">
                          <th className='px-8 py-4 text-left'>#</th>
                          <th className="px-8 py-4 text-left">Durée de chauffe</th>
                        </tr>
                        </thead>
                        <tbody>
                        {
                          currentData?.pieces_in_oven !== undefined && currentData.pieces_in_oven.map((piece, index) => (
                            <tr key={index}>
                              <td className='px-8 py-4'>{index + 1}</td>
                              <td className='px-8 py-4'>{
                                formatSecondsToMinutesSeconds(piece.time_in_oven)
                              }</td>
                            </tr>
                          ))
                        }
                        </tbody>
                      </table>
                    </div>
                  </div>

                </div>
              </div>

              {/* Right column */}
              <div className="w-1/3 h-full flex flex-col justify-between gap-8">

                <div className="flex flex-col gap-12">
                  {/* Security elements */}
                  <div className="flex flex-col gap-4 p-4 rounded-md bg-perception-black-500">
                    <h1 className="text-center p-4 font-bold bg-perception-blue rounded-md">Elements de sécurité</h1>

                    {/* Top lubrication */}
                    <div className={`h-1/3 p-8 relative rounded-md flex items-center justify-center truncate
                    ${currentData?.upper_lubrication_done ? 'bg-green-500' : 'bg-perception-gray-800'}`}>
                      {
                        currentData?.upper_lubrication_done === true ?
                          <>Lubrification haute faite</>
                          :
                          <>Lubrification haute non réalisée</>
                      }
                    </div>

                    {/* Bottom lubrication */}
                    <div className={`h-1/3 p-8 relative rounded-md flex items-center justify-center truncate
                    ${currentData?.lower_lubrication_done ? 'bg-green-500' : 'bg-perception-gray-800'}`}>
                      {
                        currentData?.lower_lubrication_done === true ?
                          <>Lubrification basse faite</>
                          :
                          <>Lubrification basse non réalisée</>
                      }
                    </div>

                    {/* Safety stand */}
                    <div className={`h-1/3 p-8 relative rounded-md flex items-center justify-center truncate
                    ${currentData?.safety_stand ? 'bg-green-500' : 'bg-perception-gray-800'}`}>
                      {
                        currentData?.safety_stand === true ?
                          <>Béquille de sécurité présente</>
                          :
                          <>Béquille de sécurité absente</>
                      }
                    </div>

                    {/*<div className="flex justify-center">*/}
                    {/*  <button className="bg-error text-white p-4 rounded-sm" onClick={handleRaiseError}>Déclarer un manquement</button>*/}
                    {/*</div>*/}
                  </div>

                  {/* Cutter zone */}
                  <div className="flex flex-col gap-4 p-4 rounded-md bg-perception-black-500">
                    <h1 className="text-center p-4 font-bold bg-perception-blue rounded-md">Zone ébavurage</h1>
                    <div className="flex flex-col p-4 rounded-md bg-perception-gray-800">
                      Durée de transfert : {currentData?.transfer_cutter !== undefined && currentData?.transfer_cutter !== null ?
                      Math.ceil(currentData.transfer_cutter * 10) / 10 + 's' : '-'}
                    </div>
                  </div>
                </div>
              </div>
            </div>
            :
            <>
              <Loader category="transparent"/>
              {/*<h2 className='my-6 text-3xl font-semibold'>Choisir une gamme</h2>*/}
              {/*<div className='flex flex-col gap-4 w-80 '>*/}
              {/*  <InputSelect*/}
              {/*    className='py-3'*/}
              {/*    items={patterns}*/}
              {/*    onChange={(pattern) => setSelectedPattern(pattern)}*/}
              {/*    itemToString={(pattern) => pattern?.name}*/}
              {/*    placeholder='Choisir une gamme'*/}
              {/*    category='secondary'*/}
              {/*    autoComplete='off'*/}
              {/*  />*/}
              {/*</div>*/}
              {/*<div className='my-6'>*/}
              {/*  <Button type='submit' category='primary-btn' size='small' onClick={handleStart}>*/}
              {/*    Démarrer*/}
              {/*  </Button>*/}
              {/*</div>*/}
            </>
        }
      </div>
    </div>
  );
};
